/**
 * 脱敏手机号组件（student-sensitive-mobile-audit:SC-01/SC-11/SC-12，design D4）。
 *
 * 默认展示脱敏手机号（中间四位掩码，服务端完成脱敏）。
 * 点击眼睛图标调明文查看端点（权限 + 审计），明文态提供复制按钮。
 * 脱敏态不提供复制原文按钮（SC-12 absence 兜底）。
 */
import { useState } from 'react';
import { Button, Space, Tooltip } from 'antd';
import { message } from '../../utils/antd-message';
import { CopyOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { studentsApi } from '../../api/students.api';
import { ApiError } from '../../api/transport';

export function MaskedMobile({
  studentId,
  masked,
}: {
  studentId: string;
  masked: string;
}) {
  const [plaintext, setPlaintext] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function toggleView() {
    if (plaintext) {
      setPlaintext(null);
      return;
    }
    setLoading(true);
    try {
      const mobile = await studentsApi.viewMobilePlaintext(studentId);
      setPlaintext(mobile);
    } catch (err) {
      if (err instanceof ApiError) {
        message.error(err.message);
      } else {
        message.error('无明文查看权限');
      }
    } finally {
      setLoading(false);
    }
  }

  async function copyMobile() {
    if (!plaintext) return;
    try {
      await navigator.clipboard.writeText(plaintext);
      message.success('已复制手机号');
    } catch {
      message.error('复制失败');
    }
  }

  return (
    <Space size={4}>
      <span data-testid={`mobile-text-${studentId}`}>{plaintext ?? masked}</span>
      <Tooltip title={plaintext ? '隐藏' : '查看明文'}>
        <Button
          type="text"
          size="small"
          loading={loading}
          onClick={toggleView}
          icon={plaintext ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          data-testid={`mobile-toggle-${studentId}`}
        />
      </Tooltip>
      {plaintext && (
        <Tooltip title="复制手机号">
          <Button
            type="text"
            size="small"
            onClick={copyMobile}
            icon={<CopyOutlined />}
            data-testid={`mobile-copy-${studentId}`}
          />
        </Tooltip>
      )}
    </Space>
  );
}
